import {EventModel, type EventTicketOption} from 'event/models/Event'

/** Default suggested ticket face value (EUR). */
export const DEFAULT_TICKET_PRICE = 5.0

/** Platform fee as a percentage of ticket face value (same as API TicketController::DEFAULT_TICKET_TAX). */
export const DEFAULT_TICKET_TAX = 20.0

/** Minimum platform fee in EUR (same as API TicketController::MIN_TICKET_TAX). */
export const MIN_TICKET_TAX = 1

/** Optional overrides from {@link Organization.economy} / platform settings API. */
export type TicketFeeSnapshot = {
  ticket_fee_percent: number
  min_ticket_fee: number
}

function orderedTickets(event: EventModel): EventTicketOption[] {
  const list = event.tickets
  if (!list?.length) {
    return []
  }
  return [...list].sort((a, b) => a.sort_order - b.sort_order || a.id - b.id)
}

/** Lowest face price among ticket tiers, or legacy `event.price`. */
export function getTicketPrice(event: EventModel): number {
  const tiers = orderedTickets(event)
  if (tiers.length) {
    return Math.min(...tiers.map((t) => t.price))
  }
  return event.price
}

export function getTicketPriceMax(event: EventModel): number {
  const tiers = orderedTickets(event)
  if (tiers.length) {
    return Math.max(...tiers.map((t) => t.price))
  }
  return event.price
}

/**
 * Human-readable price label for the event page (single price, range, or free).
 */
export function formatEventTicketPriceLabel(
  event: EventModel,
  t: (key: string) => string,
): string {
  const min = getTicketPrice(event)
  const max = getTicketPriceMax(event)
  if (min === 0 && max === 0) {
    return t('eventRow.free')
  }
  if (min === max) {
    return `${min.toFixed(2)} €`
  }
  return `${min.toFixed(2)} – ${max.toFixed(2)} €`
}

export function parseTicketFeaturesString(
  json: string | null | undefined,
): string[] {
  if (!json) return []
  try {
    const p = JSON.parse(json) as unknown
    return Array.isArray(p) ? p.map(String) : []
  } catch {
    return []
  }
}

/**
 * Platform application fee in EUR for a given ticket face price.
 */
export function computePlatformFeeEur(
  ticketFacePriceEur: number,
  snapshot?: TicketFeeSnapshot | null,
): number {
  if (ticketFacePriceEur <= 0) {
    return 0
  }
  const pct = snapshot?.ticket_fee_percent ?? DEFAULT_TICKET_TAX
  const min = snapshot?.min_ticket_fee ?? MIN_TICKET_TAX
  const calculated = (ticketFacePriceEur / 100) * pct
  return calculated <= min ? min : calculated
}

/**
 * Organizer share after the platform fee (EUR), before any organizer-side VAT.
 * VAT on invoices is determined server-side from country rules.
 */
export function organizerShareAfterPlatformFeeEur(
  ticketFacePriceEur: number,
  snapshot?: TicketFeeSnapshot | null,
): number {
  if (ticketFacePriceEur <= 0) {
    return 0
  }
  const fee = computePlatformFeeEur(ticketFacePriceEur, snapshot)
  return Math.floor((ticketFacePriceEur - fee) * 100) / 100
}

/** Per-day price for activating / extending an event (organisation or platform defaults). */
export function getEventActivationDayPriceEur(event: EventModel | null): number {
  const v = event?.organization?.economy?.display_event_price_per_day
  return typeof v === 'number' && !Number.isNaN(v) ? v : 14
}

export function eventHasDayPricePromo(event: EventModel | null): boolean {
  return Boolean(event?.organization?.economy?.has_event_day_promo)
}

export function getEventDayListAndPromoEur(
  event: EventModel | null,
): {list: number; promo: number | null; display: number} {
  const econ = event?.organization?.economy
  const list = econ?.event_price_per_day ?? 14
  const promo =
    econ?.promo_event_price_per_day != null
      ? econ.promo_event_price_per_day
      : null
  const display = getEventActivationDayPriceEur(event)
  return {list, promo, display}
}
